import { useTranslation } from '@cobliteam/i18n';
import { Button, Typography } from '@cobliteam/ui/components';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';

interface TablePaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onCallbackPaginationButton?: () => void;
  hiddenDivider?: boolean;
}

const TablePagination = ({ count, page, rowsPerPage, onPageChange, onRowsPerPageChange, onCallbackPaginationButton, hiddenDivider = false }: TablePaginationProps) => {
  const rowsPerPageOptions = [10, 25, 50, 100];
  const { t } = useTranslation('tracking');

  const from = page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, count);

  const totalPages = Math.ceil(count / rowsPerPage);

  const handlePreviousPage = () => {
    if (page > 0) {
      onPageChange(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      onPageChange(page + 1);
    }
  };

  const handleFirstPage = () => {
    onPageChange(0);
  };

  const handleLastPage = () => {
    onPageChange(totalPages - 1);
  };

  return (
    <div className='flex flex-col'>
      <div className='flex items-center justify-between px-4 py-3'>
        {onCallbackPaginationButton && (
          <Button variant='ghost' startIcon={<Icon name='mail' />} onClick={onCallbackPaginationButton}>
            {t('send_opinion')}
          </Button>
        )}

        <div className='flex items-center gap-4'>
          <div className='flex items-center gap-2'>
            <Typography variant='body'>{t('lines_per_page')}</Typography>
            <Select value={rowsPerPage.toString()} onValueChange={(value) => onRowsPerPageChange(Number(value))}>
              <SelectTrigger className='w-20'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rowsPerPageOptions.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex items-center gap-2'>
            <Typography variant='body'>
              {from} - {to} {t('of')} {count}
            </Typography>
          </div>

          <div className='flex items-center gap-1'>
            <Button variant='outlined' size='sm' onClick={handleFirstPage} disabled={page === 0} icon={<Icon name='chevron-first' size='sm' />} />
            <Button variant='outlined' size='sm' onClick={handlePreviousPage} disabled={page === 0} icon={<Icon name='chevron-left' size='sm' />} />
            <Button variant='outlined' size='sm' onClick={handleNextPage} disabled={page === totalPages - 1} icon={<Icon name='chevron-right' size='sm' />} />
            <Button variant='outlined' size='sm' onClick={handleLastPage} disabled={page === totalPages - 1} icon={<Icon name='chevron-last' size='sm' />} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
